import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "modal-body call-panel-body",
  style: {"flex":"1","overflow":"hidden","display":"flex","flex-direction":"column"}
}
const _hoisted_2 = { class: "whatsapp-layout-with-sidebar" }
const _hoisted_3 = {
  key: 0,
  class: "whatsapp-loading-state"
}
const _hoisted_4 = {
  key: 1,
  class: "whatsapp-disconnected-state"
}
const _hoisted_5 = { class: "whatsapp-disconnected-content" }
const _hoisted_6 = {
  key: 2,
  class: "whatsapp-content-wrapper"
}
const _hoisted_7 = {
  key: 0,
  class: "whatsapp-sidebar-view"
}
const _hoisted_8 = {
  key: 0,
  class: "whatsapp-sidebar-header"
}
const _hoisted_9 = {
  key: 1,
  class: "whatsapp-empty-state whatsapp-empty-state--no-conversations"
}
const _hoisted_10 = { class: "whatsapp-empty-state__content" }
const _hoisted_11 = {
  key: 1,
  class: "whatsapp-main-content"
}
const _hoisted_12 = { class: "whatsapp-message-view-wrapper" }
const _hoisted_13 = {
  key: 1,
  class: "whatsapp-empty-state"
}

import { ref, computed, watch, nextTick } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import ConversationList from "@/components/bucket/interactions/ConversationList.vue";
import WhatsAppThreadCard from "@/components/bucket/interactions/WhatsAppThreadCard.vue";
import MessageComposer from "@/components/socials/MessageComposer.vue";
import ForwardMessageModal from "@/components/socials/ForwardMessageModal.vue";
import StartWhatsAppChatModal from "@/components/socials/StartWhatsAppChatModal.vue";
import OutreachCueCards from "@/components/bucket/ai/OutreachCueCards.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import { Button } from "@/components/ui";
import Unipile from "@/services/unipile/unipile";
import { isBodyEmpty } from "@/utils/emailComposerHelpers";
import AIGenerationService from "@/services/ai/ai-generation";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";
import { normalizeWhatsAppMessages } from "@/utils/conversationNormalizer";

import { useContactData } from "@/composables/contacts/useContactData";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";
import type { Conversation, Message, Channel } from "@/dtos/Unipile.dto";
import { useQueryClient } from "@tanstack/vue-query";
import {
  useWhatsAppConversations,
  useWhatsAppMessages,
  useSendWhatsAppMessage,
  useCreateWhatsAppChat,
  useForwardWhatsAppMessage,
  useWhatsAppMessageWebhooks,
  useDeleteWhatsAppMessage,
} from "@/composables/whatsapp";
import { useReactToUnipileMessage } from "@/composables/useReactToUnipileMessage";

// Define props
interface Props {
  selectedBucket: any;
  contact?: any;
  whatsAppChannel: Channel | null;
  icps: Array<{ id: string; name: string; isDefault?: boolean }>;
  isChannelsLoading?: boolean;
  icpLoading?: boolean;
  pendingCueCardContent?: any;
  initialConversationId?: string | null;
  initialMessageId?: string | null;
}

interface Emits {
  (e: "refresh-conversations"): void;
  (e: "message-sent"): void;
  (e: "reconnect-requested"): void;
  (
    e: "open-ai-personalization",
    payload: { defaultType: string; contactName?: string; icpName?: string }
  ): void;
  (e: "switch-channel", payload: any): void;
  (e: "content-applied"): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsAppInteractionPanel',
  props: {
    selectedBucket: {},
    contact: {},
    whatsAppChannel: {},
    icps: {},
    isChannelsLoading: { type: Boolean, default: false },
    icpLoading: { type: Boolean, default: false },
    pendingCueCardContent: { default: null },
    initialConversationId: { default: null },
    initialMessageId: { default: null }
  },
  emits: ["refresh-conversations", "message-sent", "reconnect-requested", "open-ai-personalization", "switch-channel", "content-applied"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Define emits
const emit = __emit;

const queryClient = useQueryClient();
const $toast = useToast();
const resolvedAiPromptsQuery = useResolvedAiPrompts();

// Store
const store = useStore();

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

// Local state

const selectedWhatsAppConversationId = ref<string | null>(null);
const showWhatsAppDetailView = ref(false);
const selectedWhatsAppConversation = ref<Conversation | null>(null);
const replyingTo = ref<Message | null>(null);
const forwardingMessage = ref<Message | null>(null);
const showForwardModal = ref(false);
const showStartChatModal = ref(false);
const initialModalMessage = ref("");
const isGeneratingAI = ref(false);
const generatingAction = ref<string | null>(null);
const messageComposerRef = ref<InstanceType<typeof MessageComposer> | null>(null);

// Computed params for queries
const conversationsParams = computed(() => {
  const contactId = props.selectedBucket?.contact?.id;
  const accountId = props.whatsAppChannel?.accountId;

  if (!contactId || !accountId) {
    return null;
  }

  return {
    contactId,
    accountId,
    limit: 50,
    contactName:
      `${props.selectedBucket?.contact?.fname || ""} ${props.selectedBucket?.contact?.lname || ""}`.trim(),
  };
});

const messagesParams = computed(() => {
  if (!selectedWhatsAppConversationId.value) {
    return null;
  }

  return {
    conversationId: selectedWhatsAppConversationId.value,
    limit: 10,
  };
});

// Queries
const {
  data: conversationsData,
  isLoading: isLoadingConversations,
  refetch: refetchConversations,
} = useWhatsAppConversations(conversationsParams);

const {
  messages: whatsappMessages,
  isLoadingMessages,
  isLoadingOlderMessages,
  hasMoreMessages,
  handleLoadOlderMessages,
} = useWhatsAppMessages(
  messagesParams,
  computed(() => computedSelectedWhatsAppConversation.value)
);

// Mutations
const { sendMessage, isSending: isSendingMessage } = useSendWhatsAppMessage({
  messages: whatsappMessages,
});
const { createChat, isCreating: isCreatingChat } = useCreateWhatsAppChat();
const { forwardMessage: forwardMessageMutation } = useForwardWhatsAppMessage();
const { deleteMessage, isDeleting: isDeletingMessage } = useDeleteWhatsAppMessage();
const { addReaction, removeReaction } = useReactToUnipileMessage();

// Computed properties
const whatsAppConversations = computed(() => conversationsData.value || []);

const computedSelectedWhatsAppConversation = computed(() => {
  if (selectedWhatsAppConversation.value) {
    return selectedWhatsAppConversation.value;
  }
  if (!selectedWhatsAppConversationId.value) {
    return whatsAppConversations.value?.[0] || null;
  }
  return (
    whatsAppConversations.value?.find((c) => c.id === selectedWhatsAppConversationId.value) ||
    whatsAppConversations.value?.[0] ||
    null
  );
});

const whatsAppICPOptions = computed(() => {
  const options = [
    {
      value: "",
      label: "Select ICP for WhatsApp",
    },
  ];
  props.icps.forEach((icp) => {
    options.push({
      value: icp.id,
      label: icp.isDefault ? `${icp.name} (Default)` : icp.name,
    });
  });
  return options;
});

const contactPhoneNumbers = computed(() => {
  const { computeContactPhoneNumbers } = useContactData(store);
  return computeContactPhoneNumbers({ contact: props.contact ?? props.selectedBucket?.contact });
});

const availableChannelsForChat = computed(() => {
  const channels: Channel[] = [];
  if (props.whatsAppChannel?.accountId) {
    channels.push({
      id: props.whatsAppChannel.id,
      name: "WhatsApp",
      type: "whatsapp" as const,
      icon: "mdi:whatsapp",
      accountId: props.whatsAppChannel.accountId,
      isConnected: isChannelHealthyStatus(props.whatsAppChannel?.status),
      status: props.whatsAppChannel.status,
    });
  }
  return channels;
});

const forwardableConversations = computed(() => {
  const conversations = whatsAppConversations.value || [];
  const currentConversationId = computedSelectedWhatsAppConversation.value?.id;
  return conversations.filter((conv) => conv.id !== currentConversationId);
});

const isWhatsAppChannelDisconnected = computed(() => {
  if (!props.whatsAppChannel) return true;
  if (!props.whatsAppChannel.isConnected) return true;
  const status = props.whatsAppChannel.status?.toUpperCase();
  return ["DISCONNECTED", "ERROR"].includes(status || "");
});

// Utility functions
function isChannelHealthyStatus(status?: string): boolean {
  if (!status) return false;
  const upperStatus = status.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(upperStatus);
}

// Methods
async function applyWhatsAppContent(content: string) {
  await nextTick();
  if (
    messageComposerRef.value &&
    typeof (messageComposerRef.value as any).setContent === "function"
  ) {
    (messageComposerRef.value as any).setContent(content);
  }
  $toast?.success("WhatsApp message loaded from cue card");
}

async function handleUseCueCardContent(payload: {
  channel: string;
  content: string;
  subject?: string;
}) {
  if (payload.channel === "whatsapp" && payload.content) {
    if (showWhatsAppDetailView.value && computedSelectedWhatsAppConversation.value) {
      await applyWhatsAppContent(payload.content);
    } else {
      initialModalMessage.value = payload.content;
      showStartChatModal.value = true;
    }
  } else {
    emit("switch-channel", payload);
  }
}

async function handleWhatsAppConversationSelected(conversation: any) {
  selectedWhatsAppConversationId.value = conversation.id;
  selectedWhatsAppConversation.value = {
    ...conversation,
    channel: "whatsapp",
    contactName: conversation.contactName || conversation.title || "Unknown",
  };
  showWhatsAppDetailView.value = true;

  const contactId = props.selectedBucket?.contact?.id;

  if (conversation.unreadCount > 0) {
    const prevUnread = conversation.unreadCount;
    const p = conversationsParams.value;
    const convQueryKey = p
      ? ["whatsapp-conversations", p.contactId, p.accountId, p.limit || 50]
      : null;

    if (convQueryKey) {
      queryClient.setQueryData<any[]>(convQueryKey, (old) => {
        if (!old) return old;
        return old.map((c: any) =>
          c.id === conversation.id ? { ...c, unreadCount: 0, unread_count: 0 } : c
        );
      });
    }

    if (contactId) {
      queryClient.setQueryData(["interactionCounts", contactId], (old: any) => {
        if (!old?.whatsapp) return old;
        return {
          ...old,
          whatsapp: {
            ...old.whatsapp,
            unread: Math.max(0, (old.whatsapp.unread || 0) - prevUnread),
          },
        };
      });
    }
  }

  // Always mark the chat read on open, regardless of what Unipile's freshly-fetched
  // conversation list reports as `unreadCount`. That value can be out of sync with our own
  // chat.unreadCount (which only ever increments on inbound-message webhooks) — gating this
  // call on it meant a chat Unipile already considered "read" would never trigger our own
  // reset, leaving the Activity column's count stuck forever. performChatAction is a safe
  // no-op when the chat is already marked read.
  try {
    await Unipile.performChatAction(conversation.id, {
      action: "setReadStatus",
      value: true,
    });
    // Buckets table's Activity column is a separate bulk query, keyed by the page's
    // contactIds — the backend already zeroes chat.unreadCount on setReadStatus, but that
    // query's cache is untouched by the optimistic updates above, so invalidate it too.
    queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
  } catch (error) {
    console.error("Failed to mark conversation as read:", error);
    queryClient.invalidateQueries({ queryKey: ["whatsapp-conversations"] });
    if (contactId) {
      queryClient.invalidateQueries({ queryKey: ["interactionCounts", contactId] });
    }
  }
}

function handleWhatsAppBackToList() {
  selectedWhatsAppConversationId.value = null;
  selectedWhatsAppConversation.value = null;
  showWhatsAppDetailView.value = false;
  refetchConversations();
}

function handleWhatsAppReply(message?: Message) {
  replyingTo.value = message || null;
}

async function handleWhatsAppAiReply(payload?: any) {
  if (!computedSelectedWhatsAppConversation.value || isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "reply";
  try {
    const conversationHistory = normalizeWhatsAppMessages(whatsappMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateReply({
      contactId,
      icpId,
      channel: "whatsapp",
      conversationHistory,
    });

    // Populate composer using exposed method
    if (messageComposerRef.value) {
      (messageComposerRef.value as any).setContent?.(response.body);
    }

    $toast?.success("AI reply generated");
  } catch (error) {
    console.error("Failed to generate AI reply:", error);
    $toast?.error("Failed to generate AI response");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
}

async function handleWhatsAppAiFollowup(payload?: any) {
  if (!computedSelectedWhatsAppConversation.value || isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "followup";
  try {
    const conversationHistory = normalizeWhatsAppMessages(whatsappMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateFollowup({
      contactId,
      icpId,
      channel: "whatsapp",
      conversationHistory,
    });

    // Populate composer using exposed method
    if (messageComposerRef.value) {
      (messageComposerRef.value as any).setContent?.(response.body);
    }

    $toast?.success("AI follow-up generated");
  } catch (error) {
    console.error("Failed to generate AI follow-up:", error);
    $toast?.error("Failed to generate AI response");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
}

async function handleWhatsAppAiCompose(payload?: any) {
  if (isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "compose";
  try {
    const conversationHistory = normalizeWhatsAppMessages(whatsappMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateCompose({
      contactId,
      icpId,
      channel: "whatsapp",
      conversationHistory,
      customInstructions: payload?.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    if (messageComposerRef.value) {
      (messageComposerRef.value as any).setContent?.(response.body);
    }

    $toast?.success("AI message composed");
  } catch (error) {
    console.error("Failed to generate AI compose:", error);
    $toast?.error("Failed to generate AI response");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
}

function handleWhatsAppForward(message: Message) {
  forwardingMessage.value = message;
  showForwardModal.value = true;
}

async function handleForwardConfirm(chatId: string) {
  if (!forwardingMessage.value) return;

  try {
    if (
      forwardingMessage.value.channel !== "whatsapp" &&
      forwardingMessage.value.channel !== "linkedin"
    ) {
      return;
    }

    showForwardModal.value = false;
    const messageToForward = forwardingMessage.value;
    forwardingMessage.value = null;

    await forwardMessageMutation({
      messageId: messageToForward.id,
      chat_id: chatId,
    });

    $toast?.success("Message forwarded successfully");
  } catch (error: any) {
    console.error("Failed to forward message:", error);
    $toast?.error(error.response?.data?.message || "Failed to forward message");
  }
}

function handleForwardSearch(query: string) {
  // Placeholder
}

function handleForwardLoadMore() {
  // Placeholder
}

const showDeleteConfirmation = ref(false);
const messageToDelete = ref<Message | null>(null);

function handleDeleteMessage(message: Message) {
  messageToDelete.value = message;
  showDeleteConfirmation.value = true;
}

async function confirmDeleteMessage() {
  const message = messageToDelete.value;
  if (!message) return null;

  try {
    await deleteMessage(message.id);
    const idx = whatsappMessages.value.findIndex((m: any) => m.id === message.id);
    if (idx !== -1) whatsappMessages.value[idx].deleted = true;
    $toast?.success("Message deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Failed to delete message:", error);
    $toast?.error(error.response?.data?.message || "Failed to delete message");
    throw error;
  }
}

function handleDeleteConfirmationClose() {
  showDeleteConfirmation.value = false;
  messageToDelete.value = null;
}

async function handleReactToMessage(message: Message, reaction: string) {
  try {
    const existing = whatsappMessages.value.find((m: any) => m.id === message.id);
    const ownReaction = (existing as any)?.reactions?.find((r: any) => r.is_sender);
    const isRemoving = ownReaction?.value === reaction;

    const reactions = isRemoving
      ? await removeReaction(message.id)
      : await addReaction(message.id, reaction);

    const idx = whatsappMessages.value.findIndex((m: any) => m.id === message.id);
    if (idx !== -1) {
      whatsappMessages.value[idx].reactions = reactions;
    }
  } catch (error: any) {
    console.error("Failed to react to message:", error);
    $toast?.error(error.response?.data?.message || "Failed to react to message");
  }
}

async function handleRemoveReaction(message: Message) {
  try {
    const reactions = await removeReaction(message.id);
    const idx = whatsappMessages.value.findIndex((m: any) => m.id === message.id);
    if (idx !== -1) {
      whatsappMessages.value[idx].reactions = reactions;
    }
  } catch (error: any) {
    console.error("Failed to remove reaction:", error);
    $toast?.error(error.response?.data?.message || "Failed to remove reaction");
  }
}

async function handleCreateChat(request: any) {
  try {
    const response = await createChat(request);

    if (response?.data) {
      const newConversation = response.data;
      $toast?.success("Chat created successfully");
      showStartChatModal.value = false;

      // Refetch conversations
      await refetchConversations();

      if (newConversation.id) {
        // Wait a bit for the query to update
        await new Promise((resolve) => setTimeout(resolve, 500));

        const createdConv = whatsAppConversations.value.find((c) => c.id === newConversation.id);
        if (createdConv) {
          selectedWhatsAppConversationId.value = createdConv.id;
          selectedWhatsAppConversation.value = {
            ...createdConv,
            channel: "whatsapp",
          };
          showWhatsAppDetailView.value = true;
        } else {
          try {
            const chatResponse = await Unipile.getChat(newConversation.id);
            if (chatResponse.data?.data) {
              const chat = chatResponse.data.data as any;
              const contactName = chat.name || chat.title || "Unknown";
              selectedWhatsAppConversationId.value = chat.id;
              selectedWhatsAppConversation.value = {
                ...chat,
                id: chat.id,
                channel: "whatsapp",
                contactName: contactName,
                title: contactName,
                lastMessage: chat.lastMessage || "",
                lastMessageAt: chat.timestamp || new Date().toISOString(),
                unreadCount: chat.unread_count || 0,
                participants: chat.participants || [],
              } as Conversation;
              showWhatsAppDetailView.value = true;
            }
          } catch (error) {
            console.error("Failed to fetch created chat:", error);
          }
        }
      }
    }
  } catch (error: any) {
    console.error("Failed to create chat:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to create chat";
    $toast?.error(errorMessage);
  }
}

async function handleSendMessage(data: {
  text?: string;
  attachments?: File[];
  voice_message?: File;
  video_message?: File;
}) {
  await showUsageBasedFeaturesModalOnce("whatsapp_message", async () => {
    const conversation = computedSelectedWhatsAppConversation.value;

    if (!conversation || !conversation.id) {
      $toast?.error("No conversation selected");
      return;
    }

    if (isSendingMessage.value) {
      return;
    }

    const channel = props.whatsAppChannel;
    if (!channel?.accountId) {
      $toast?.error("WhatsApp channel not connected");
      return;
    }

    // Check for files
    const hasAttachments =
      data.attachments && Array.isArray(data.attachments) && data.attachments.length > 0;
    const hasVoice = data.voice_message && data.voice_message instanceof File;
    const hasVideo = data.video_message && data.video_message instanceof File;
    const hasFiles = hasAttachments || hasVoice || hasVideo;

    // Handle text content
    const textContent = data.text || "";
    const isEmptyText = isBodyEmpty(textContent);

    if (!hasFiles && isEmptyText) {
      return;
    }

    const cleanedContent = isEmptyText ? "" : textContent;

    try {
      await sendMessage({
        conversationId: conversation.id,
        text: cleanedContent || undefined,
        quote_id: replyingTo.value?.id || (replyingTo.value as any)?.unipileMessageId || undefined,
        attachments: data.attachments,
        voice_message: data.voice_message,
        video_message: data.video_message,
      });

      replyingTo.value = null;
      emit("message-sent");
    } catch (error: any) {
      console.error("Failed to send message:", error);
      if (!error?.absorbedButRecoverable) {
        $toast?.error(error.response?.data?.message || "Failed to send message");
      }
    }
  });
}

// Webhooks setup
useWhatsAppMessageWebhooks({
  selectedConversation: computed(() => computedSelectedWhatsAppConversation.value),
  messages: whatsappMessages,
});

// Watchers
watch(
  () => props.whatsAppChannel,
  (newChannel) => {
    if (newChannel) {
      refetchConversations();
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedBucket,
  () => {
    if (props.whatsAppChannel) {
      refetchConversations();
    }
  },
  { immediate: true }
);

// Emit refresh when conversations are loaded
watch(
  () => conversationsData.value,
  () => {
    if (conversationsData.value) {
      emit("refresh-conversations");
    }
  }
);

watch(
  () => props.pendingCueCardContent,
  async (payload) => {
    if (payload && payload.channel === "whatsapp" && payload.content) {
      if (showWhatsAppDetailView.value && computedSelectedWhatsAppConversation.value) {
        await applyWhatsAppContent(payload.content);
      } else {
        initialModalMessage.value = payload.content;
        showStartChatModal.value = true;
      }
      emit("content-applied");
    }
  }
);

// Watch initialConversationId to auto-select conversation, e.g. from clicking an
// Interaction History entry. The target conversation may not be in the currently
// loaded/paginated list (older chats, or the list simply hasn't loaded yet), so once
// the list has settled without a match, fetch that one conversation directly by id.
let lastAppliedWhatsAppConversationId: string | null = null;
watch(
  [() => whatsAppConversations.value, () => props.initialConversationId, isLoadingConversations],
  async ([conversations, initialId, loadingConversations]) => {
    if (!initialId || initialId === lastAppliedWhatsAppConversationId) return;

    const match = conversations?.find((c) => c.unipileChatId === initialId || c.id === initialId);
    if (match) {
      handleWhatsAppConversationSelected(match);
      lastAppliedWhatsAppConversationId = initialId;
      return;
    }

    if (loadingConversations) return;
    const accountId = props.whatsAppChannel?.accountId;
    if (!accountId) return;

    try {
      const response = await Unipile.getChat(initialId, accountId);
      if (response?.data?.data && initialId === props.initialConversationId) {
        handleWhatsAppConversationSelected(response.data.data as any);
        lastAppliedWhatsAppConversationId = initialId;
      }
    } catch (error) {
      console.error("Failed to load WhatsApp conversation from Interaction History", error);
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (__props.isChannelsLoading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(LoadingBars, { size: "sm" })
          ]))
        : (!__props.whatsAppChannel?.isConnected || isWhatsAppChannelDisconnected.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:link-variant-off",
                  class: "whatsapp-disconnected-icon"
                }),
                _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "whatsapp-disconnected-title" }, "Not Connected", -1)),
                _cache[7] || (_cache[7] = _createElementVNode("p", { class: "whatsapp-disconnected-message" }, " Please connect your WhatsApp account to view conversations. ", -1)),
                _createElementVNode("button", {
                  class: "whatsapp-disconnected-action",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('reconnect-requested')))
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:link" }),
                  _cache[5] || (_cache[5] = _createTextVNode(" Connect Account ", -1))
                ])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(OutreachCueCards, {
                "icp-options": whatsAppICPOptions.value,
                "icp-loading": props.icpLoading,
                "contact-id": __props.selectedBucket?.contact?.id || '',
                "contact-name": 
            [__props.selectedBucket?.contact?.fname, __props.selectedBucket?.contact?.lname]
              .filter(Boolean)
              .join(' ')
          ,
                "full-width": true,
                onUseContent: handleUseCueCardContent
              }, null, 8, ["icp-options", "icp-loading", "contact-id", "contact-name"]),
              (!showWhatsAppDetailView.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    (__props.whatsAppChannel)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                          _createVNode(_unref(Button), {
                            variant: "primary",
                            size: "sm",
                            icon: "mdi:chat-plus",
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (showStartChatModal.value = true)),
                            block: "",
                            disabled: !isChannelHealthyStatus(__props.whatsAppChannel?.status)
                          }, {
                            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                              _createTextVNode(" Start New Chat ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled"])
                        ]))
                      : _createCommentVNode("", true),
                    (!_unref(isLoadingConversations) && whatsAppConversations.value.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _createElementVNode("div", _hoisted_10, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:whatsapp",
                              class: "whatsapp-empty-state__icon"
                            }),
                            _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "whatsapp-empty-state__title" }, "No Conversations Found", -1)),
                            _cache[10] || (_cache[10] = _createElementVNode("p", { class: "whatsapp-empty-state__message" }, " Start a conversation with this contact to view messages here. ", -1))
                          ])
                        ]))
                      : (_openBlock(), _createBlock(ConversationList, {
                          key: 2,
                          conversations: whatsAppConversations.value as any,
                          "selected-conversation-id": selectedWhatsAppConversationId.value ?? undefined,
                          "is-loading": _unref(isLoadingConversations),
                          type: "whatsapp",
                          "show-search": false,
                          onConversationSelected: handleWhatsAppConversationSelected
                        }, null, 8, ["conversations", "selected-conversation-id", "is-loading"]))
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      (computedSelectedWhatsAppConversation.value)
                        ? (_openBlock(), _createBlock(WhatsAppThreadCard, {
                            key: 0,
                            "contact-name": 
                (computedSelectedWhatsAppConversation.value as any)?.contactName ||
                (computedSelectedWhatsAppConversation.value as any)?.title
              ,
                            "last-seen": computedSelectedWhatsAppConversation.value.lastSeen || 'Last seen recently',
                            "unipile-messages": _unref(whatsappMessages),
                            "is-loading": _unref(isLoadingMessages),
                            "is-loading-older-messages": _unref(isLoadingOlderMessages),
                            "has-more-messages": _unref(hasMoreMessages),
                            "show-back-button": true,
                            "highlight-message-id": props.initialMessageId,
                            onBack: handleWhatsAppBackToList,
                            onReply: handleWhatsAppReply,
                            onAiReply: handleWhatsAppAiReply,
                            onAddFollowup: handleWhatsAppAiFollowup,
                            onForward: handleWhatsAppForward,
                            onDelete: handleDeleteMessage,
                            onReact: handleReactToMessage,
                            onRemoveReaction: handleRemoveReaction,
                            onLoadOlderMessages: _unref(handleLoadOlderMessages)
                          }, null, 8, ["contact-name", "last-seen", "unipile-messages", "is-loading", "is-loading-older-messages", "has-more-messages", "highlight-message-id", "onLoadOlderMessages"]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_13, [...(_cache[11] || (_cache[11] = [
                            _createElementVNode("p", null, "Select a conversation to view messages", -1)
                          ]))])),
                      (computedSelectedWhatsAppConversation.value)
                        ? (_openBlock(), _createBlock(MessageComposer, {
                            key: 2,
                            ref_key: "messageComposerRef",
                            ref: messageComposerRef,
                            conversation: computedSelectedWhatsAppConversation.value,
                            "reply-to": replyingTo.value,
                            "channel-status": 
                __props.whatsAppChannel && __props.whatsAppChannel.status ? __props.whatsAppChannel.status : undefined
              ,
                            "is-sending": _unref(isSendingMessage),
                            "show-ai-buttons": true,
                            "ai-buttons-disabled": 
                !isChannelHealthyStatus(__props.whatsAppChannel?.status) || _unref(isUsageBlocked)
              ,
                            "is-generating-a-i": isGeneratingAI.value,
                            "generating-action": generatingAction.value,
                            "contact-id": __props.selectedBucket?.contact?.id,
                            "icp-id": __props.icps?.find((i) => i.isDefault)?.id,
                            icps: __props.icps,
                            "channel-name": 'whatsapp',
                            onSendMessage: handleSendMessage,
                            onCancelReply: _cache[2] || (_cache[2] = ($event: any) => (replyingTo.value = null)),
                            onAiReply: handleWhatsAppAiReply,
                            onAiFollowup: handleWhatsAppAiFollowup,
                            onAiCompose: handleWhatsAppAiCompose
                          }, null, 8, ["conversation", "reply-to", "channel-status", "is-sending", "ai-buttons-disabled", "is-generating-a-i", "generating-action", "contact-id", "icp-id", "icps"]))
                        : _createCommentVNode("", true)
                    ])
                  ]))
            ])),
      _createVNode(StartWhatsAppChatModal, {
        show: showStartChatModal.value,
        channels: availableChannelsForChat.value,
        "default-account-id": __props.whatsAppChannel?.accountId,
        "is-loading": _unref(isCreatingChat),
        "auto-set-channel-type": 'whatsapp',
        "hide-account-selection": true,
        "contact-phone-numbers": contactPhoneNumbers.value,
        "initial-message": initialModalMessage.value,
        "contact-id": __props.selectedBucket?.contact?.id,
        "icp-id": __props.icps?.find((i) => i.isDefault)?.id,
        onClose: _cache[3] || (_cache[3] = ($event: any) => {
          showStartChatModal.value = false;
          initialModalMessage.value = '';
        }),
        onCreateChat: handleCreateChat
      }, null, 8, ["show", "channels", "default-account-id", "is-loading", "contact-phone-numbers", "initial-message", "contact-id", "icp-id"]),
      _createVNode(ForwardMessageModal, {
        show: showForwardModal.value,
        conversations: forwardableConversations.value,
        "channel-type": 'whatsapp',
        "is-loading": _unref(isLoadingConversations),
        "is-loading-more": false,
        "has-more": false,
        onClose: _cache[4] || (_cache[4] = ($event: any) => (showForwardModal.value = false)),
        onForward: handleForwardConfirm,
        onSearch: handleForwardSearch,
        onLoadMore: handleForwardLoadMore
      }, null, 8, ["show", "conversations", "is-loading"]),
      (showDeleteConfirmation.value)
        ? (_openBlock(), _createBlock(DeleteConfirmationModal, {
            key: 3,
            entity: "message",
            "delete-function": confirmDeleteMessage,
            onCloseDeleteConfirmationModal: handleDeleteConfirmationClose
          }))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})